import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Banknote, Briefcase, ArrowRight, ArrowLeft, Loader2, Layers, Building2 } from "lucide-react";
import { PublicHeader } from "../components/PublicHeader";
import { SiteFooter } from "../components/SiteFooter";
import { api } from "../lib/api";

export default function JobDetail() {
  const { id } = useParams();
  const [role, setRole] = useState(undefined); // undefined=loading, null=not found

  useEffect(() => {
    api.get(`/jobs/${id}`).then((r) => setRole(r.data)).catch(() => setRole(null));
  }, [id]);

  return (
    <div className="min-h-screen bg-[#f5f3ee]" data-testid="job-detail-page">
      <PublicHeader />
      <main className="mx-auto max-w-3xl px-6 pb-24 pt-28">
        <Link to="/jobs" data-testid="job-back-link" className="inline-flex items-center gap-1.5 text-sm font-medium text-[#7C8514] hover:underline">
          <ArrowLeft className="h-4 w-4" /> All open roles
        </Link>

        {role === undefined ? (
          <div className="flex justify-center py-24" data-testid="job-detail-loading"><Loader2 className="h-8 w-8 animate-spin text-[#0d2f4d]" /></div>
        ) : role === null ? (
          <div className="mt-10 rounded-2xl border border-dashed border-[#cfcbbc] bg-white/60 py-20 text-center" data-testid="job-detail-notfound">
            <p className="text-lg font-medium text-[#0d2f4d]">This role is no longer open</p>
            <p className="mt-2 text-sm text-[#51637a]">It may have been filled or unpublished.</p>
            <Link to="/jobs" className="mt-6 inline-flex rounded-full bg-[#0d2f4d] px-6 py-3 text-sm font-semibold text-white hover:bg-[#1c4d78]">
              Browse open roles
            </Link>
          </div>
        ) : (
          <article className="mt-6 rounded-3xl border border-[#e3e0d3] bg-white p-8 shadow-sm sm:p-10" data-testid="job-detail-card">
            <span className="mb-5 inline-flex w-fit items-center gap-1.5 rounded-full bg-[#0d2f4d] px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#C1CC24]">
              <Briefcase className="h-3 w-3" /> {role.type}
            </span>
            <h1 className="font-heading text-3xl font-bold tracking-tight text-[#0d2f4d] sm:text-4xl">{role.title}</h1>
            {role.company && <p className="mt-1 text-base font-medium text-[#7C8514]" data-testid="job-detail-company">{role.company}</p>}
            <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-sm text-[#51637a]" data-testid="job-detail-meta">
              <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#7C8514]" /> {role.city}</p>
              {role.salary_range && <p className="flex items-center gap-2"><Banknote className="h-4 w-4 text-[#7C8514]" /> {role.salary_range}</p>}
              {role.seniority && <p className="flex items-center gap-2"><Layers className="h-4 w-4 text-[#7C8514]" /> {role.seniority}</p>}
              {role.industry && <p className="flex items-center gap-2"><Building2 className="h-4 w-4 text-[#7C8514]" /> {role.industry}</p>}
            </div>
            <div className="mt-4 flex flex-wrap gap-2" data-testid="job-detail-chips">
              {role.type && <span className="rounded-full bg-[#f0ede4] px-3 py-1 text-xs font-medium text-[#5c6410]">{role.type}</span>}
              {role.remote_ok && <span className="rounded-full bg-[#e6ecc4] px-3 py-1 text-xs font-medium text-[#5c6410]">Remote OK</span>}
              {(role.tags || []).map((t, i) => (
                <span key={i} className="rounded-full bg-[#f0ede4] px-3 py-1 text-xs font-medium text-[#51637a]">{t}</span>
              ))}
            </div>
            {role.created_at && (
              <p className="mt-3 text-xs text-[#8a8578]" data-testid="job-detail-posted">
                Posted {new Date(role.created_at).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}
              </p>
            )}
            <div className="mt-8 whitespace-pre-line text-[15px] leading-relaxed text-[#33475b]" data-testid="job-detail-description">
              {role.description}
            </div>
            {role.requirements?.length > 0 && (
              <div className="mt-8" data-testid="job-detail-requirements">
                <h2 className="font-heading text-lg font-bold text-[#0d2f4d]">What you'll bring</h2>
                <ul className="mt-3 space-y-2">
                  {role.requirements.map((r, i) => (
                    <li key={i} className="flex gap-2.5 text-[15px] leading-relaxed text-[#33475b]">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#C1CC24]" />{r}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {role.nice_to_haves?.length > 0 && (
              <div className="mt-8" data-testid="job-detail-nicetohaves">
                <h2 className="font-heading text-lg font-bold text-[#0d2f4d]">Nice to have</h2>
                <ul className="mt-3 space-y-2">
                  {role.nice_to_haves.map((r, i) => (
                    <li key={i} className="flex gap-2.5 text-[15px] leading-relaxed text-[#33475b]">
                      <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#7C8514]" />{r}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="mt-10 border-t border-[#f0ede4] pt-8">
              <Link
                to={`/signup/candidate?job=${role.id}`}
                data-testid="job-detail-apply"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-[#C1CC24] px-7 py-3.5 text-sm font-semibold text-[#0d2f4d] transition-colors hover:bg-[#aeb81f]"
              >
                Apply for this role <ArrowRight className="h-4 w-4" />
              </Link>
              <p className="mt-3 text-xs text-[#51637a]">Takes about a minute — we'll match you and take it from there.</p>
            </div>
          </article>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
